/**
 * The audit log.
 *
 * Append-only, and the screen says so rather than assuming an operator knows.
 * There is no edit affordance anywhere on this page and no delete: the log's
 * value is entirely in the fact that nothing on it can be changed, and an
 * interface that offered a row action would undermine that claim before the
 * backend ever got the chance.
 *
 * It is long — a busy install writes thousands of rows a day — so it is read one
 * server-cut page at a time. The trail is cursor-paginated: the server returns
 * the cursor for the *next* page and whether one exists, but never a total or an
 * offset, so navigation is prev/next over the cursors we have actually walked,
 * not a jump to an arbitrary page number. A bounded page is short enough to
 * render whole, which is why the `@tanstack/react-virtual` windowing this file
 * used to carry is gone: the table sizes to its rows and the page body scrolls.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { api, type PageResponse } from '@/api/client'
import { Page, PageHeader, Panel, Badge, Button, type Tone } from '@/components/primitives'
import { QueryState, Toolbar, TextField, SelectField, Notice, CopyButton } from '@/components/controls'
import { useT, useLocale } from '@/i18n/context'
import { formatDateTime } from '@/i18n'
import type { ID, ISOTime, Meta } from '@/types/domain'
import './AuditPage.css'

/**
 * One row of the trail, mirroring domain.AuditEvent.
 *
 * It used to declare `actorType`, `projectName`, `accountProfileId`,
 * `accountLabel`, `modelId`, `modelName` and `hostName`. The server sends none
 * of them: the actor's kind is `actorKind`, and the trail records ids only —
 * there is no join on the way out, so every "name" here rendered as the id
 * fallback that sat behind it. The five real subjects are project, task,
 * session, host and the generic target pair.
 */
interface AuditEntry extends Meta {
  action: string
  actor: string
  actorKind: string
  projectId?: ID
  taskId?: ID
  sessionId?: ID
  hostId?: ID
  targetType?: string
  targetId?: ID
  /** SUCCESS, FAILURE or DENIED today, but a plain string on the wire — a new
   *  result would otherwise render as a missing translation key. */
  result: string
  clientIp?: string
  userAgent?: string
  correlationId?: string
  detail?: string
  occurredAt: ISOTime
}

/** Page sizes the operator can pick. 50 mirrors the server's own default limit;
 *  200 is its ceiling (a larger request is served fewer rows, so offering more
 *  would promise a page the backend will not cut). */
const PAGE_SIZES = [25, 50, 100, 200] as const

/** The results the server writes today, from api.AuditSuccess and its siblings.
 *  Anything else is shown verbatim rather than translated. */
const KNOWN_RESULTS = ['SUCCESS', 'FAILURE', 'DENIED'] as const
type KnownResult = (typeof KNOWN_RESULTS)[number]

function isKnownResult(result: string): result is KnownResult {
  return (KNOWN_RESULTS as readonly string[]).includes(result)
}

function resultTone(result: string): Tone {
  switch (result) {
    case 'SUCCESS':
      return 'ok'
    case 'FAILURE':
      return 'err'
    case 'DENIED':
      return 'warn'
    default:
      return 'unknown'
  }
}

export function AuditPage() {
  const t = useT()
  const { locale } = useLocale()
  const scrollRef = useRef<HTMLDivElement>(null)

  const [actor, setActor] = useState('')
  const [action, setAction] = useState('')
  const [targetType, setTargetType] = useState('')
  const [pageSize, setPageSize] = useState(50)

  // Cursor-stack pagination. The server hands back the cursor for the *next* page
  // and whether one exists — never a total or an offset — so there is no honest
  // "page 7 of 40", only forward and back over the cursors we have walked.
  // cursors[i] is the cursor that fetches page i; page 0 is the empty cursor, the
  // newest rows.
  const [cursors, setCursors] = useState<string[]>([''])
  const pageIndex = cursors.length - 1
  const cursor = cursors[pageIndex]

  // A filter or page-size change describes a different result set, so the cursors
  // we stacked — positions in the *old* set — are meaningless and we return to
  // the first page. An effect rather than wrapping each setter, so a filter added
  // later is covered without anyone remembering to reset here.
  useEffect(() => {
    setCursors([''])
  }, [actor, action, targetType, pageSize])

  // Filters are server-side: a cursor-paginated stream cannot be filtered in
  // the browser without silently hiding matches that live on an unfetched page.
  //
  // Which is why the result and correlation-id filters are gone rather than
  // moved into the browser. handleListAudit builds domain.AuditFilter from
  // actor, action, targetType, projectId, taskId, from and to — it never reads
  // `result` or `correlationId`, so those two controls sent parameters the
  // server discarded and then presented the unfiltered trail as the answer.
  // Choosing DENIED and being shown every SUCCESS is worse than not being
  // offered the choice.
  const audit = useQuery({
    queryKey: ['audit', actor, action, targetType, pageSize, cursor],
    queryFn: () =>
      api.get<PageResponse<AuditEntry>>('/audit', {
        cursor: cursor || undefined,
        limit: pageSize,
        actor: actor.trim() || undefined,
        action: action.trim() || undefined,
        targetType: targetType.trim() || undefined,
      }),
    // Keep the current page on screen while the next one loads, so paging does
    // not flash the empty state between server cuts.
    placeholderData: keepPreviousData,
  })

  const rows = audit.data?.items ?? []
  const canPrev = pageIndex > 0
  const canNext = Boolean(audit.data?.hasMore && audit.data.nextCursor)
  const paging = audit.isFetching && audit.isPlaceholderData

  // A page change returns the row region to its first row: paging forward from the
  // foot of one cut should open the next at its top, not mid-scroll.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 })
  }, [cursor, pageSize])

  function goNext() {
    const next = audit.data?.nextCursor
    if (audit.data?.hasMore && next) setCursors((c) => [...c, next])
  }
  function goPrev() {
    setCursors((c) => (c.length > 1 ? c.slice(0, -1) : c))
  }

  const filtersApplied = actor.trim() !== '' || action.trim() !== '' || targetType.trim() !== ''

  return (
    <Page
      header={
        <PageHeader
          title={t('audit.title')}
          subtitle={t('audit.pageStatus', { page: pageIndex + 1, count: rows.length })}
          actions={
            <Button size="sm" variant="ghost" onClick={() => void audit.refetch()}>
              {t('action.refresh')}
            </Button>
          }
        >
          <Notice tone="info">{t('audit.immutable')}</Notice>

          <Toolbar
            actions={
              filtersApplied ? (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setActor('')
                    setAction('')
                    setTargetType('')
                  }}
                >
                  {t('common.clearFilters')}
                </Button>
              ) : undefined
            }
          >
            {/* All three match exactly, because the repository compares them for
            equality — `auth` finds nothing, `auth.login` finds eight. The hints
            say so, and every value is one click of the copy button away in the
            table below. */}
            <TextField
              label={t('audit.actor')}
              type="search"
              value={actor}
              onChange={setActor}
              placeholder={t('audit.actorPlaceholder')}
              hint={t('audit.exactMatch')}
              mono
            />
            <TextField
              label={t('audit.action')}
              type="search"
              value={action}
              onChange={setAction}
              placeholder={t('audit.actionPlaceholder')}
              hint={t('audit.exactMatch')}
              mono
            />
            <TextField
              label={t('audit.targetType')}
              type="search"
              value={targetType}
              onChange={setTargetType}
              placeholder={t('audit.targetTypePlaceholder')}
              hint={t('audit.exactMatch')}
              mono
            />
          </Toolbar>
        </PageHeader>
      }
    >
      <Panel flush className="k-auditcard">
        <QueryState
          pending={audit.isPending}
          error={audit.error}
          onRetry={() => void audit.refetch()}
          empty={rows.length === 0}
          emptyTitle={filtersApplied ? t('common.noMatches') : t('audit.empty')}
          emptyHint={filtersApplied ? t('common.noMatchesHint') : t('audit.emptyHint')}
        >
          <div className="k-audit" data-paging={paging ? '' : undefined}>
            <div className="k-audit__scroll" ref={scrollRef}>
              <table className="k-table k-audit__table">
                <caption className="k-visually-hidden">{t('audit.title')}</caption>
                <thead>
                  <tr>
                    <th scope="col">{t('common.time')}</th>
                    <th scope="col">{t('audit.actor')}</th>
                    <th scope="col">{t('audit.action')}</th>
                    <th scope="col">{t('audit.target')}</th>
                    <th scope="col">{t('audit.client')}</th>
                    <th scope="col">{t('audit.result')}</th>
                    <th scope="col">{t('audit.correlation')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td className="k-mono k-audit__time">
                        {formatDateTime(row.occurredAt, locale)}
                      </td>
                      {/* `actorKind` sits beside the actor rather than under it:
                          the rows are one line tall, and it cannot be inferred
                          from the actor string either — most are prefixed
                          (`user:koraykaradas`) but the system's own writes are
                          not (`bootstrap`, kind `system`). */}
                      <td className="k-td--strong">
                        <span className="k-inline">
                          <Badge tone="neutral" subtle>
                            {row.actorKind}
                          </Badge>
                          <span className="k-mono">{row.actor}</span>
                        </span>
                      </td>
                      {/* `detail` is the row's own sentence about itself and is
                          usually the only thing separating two rows with the same
                          action. It rides in the title so the row stays a single
                          scannable line. */}
                      <td className="k-mono" title={row.detail}>
                        {row.action}
                      </td>
                      <td>
                        <AuditTarget entry={row} />
                      </td>
                      <td className="k-mono k-audit__ip">
                        {row.clientIp ?? <span className="k-dash">—</span>}
                      </td>
                      <td>
                        <Badge tone={resultTone(row.result)} subtle>
                          {isKnownResult(row.result)
                            ? t(`audit.result.${row.result}`)
                            : row.result}
                        </Badge>
                      </td>
                      <td>
                        {row.correlationId ? (
                          <span className="k-inline">
                            <span className="k-mono k-audit__corr">{row.correlationId}</span>
                            <CopyButton value={row.correlationId} ariaLabel={t('common.copy')} />
                          </span>
                        ) : (
                          <span className="k-dash">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* One server cut at a time. There is no total to jump within, so the
                controls are honest about that: page size, prev/next, and which
                page we are on — nothing that would imply a known last page. */}
            <div className="k-audit__pager">
              <SelectField
                label={t('audit.perPage')}
                labelHidden
                value={String(pageSize)}
                onChange={(v) => setPageSize(Number(v))}
                options={PAGE_SIZES.map((n) => ({
                  value: String(n),
                  label: t('audit.perPageOption', { count: n }),
                }))}
              />
              <div className="k-audit__pager-nav">
                <Button size="sm" variant="ghost" onClick={goPrev} disabled={!canPrev}>
                  {t('action.back')}
                </Button>
                <span className="k-audit__pager-page k-mono" aria-live="polite">
                  {t('audit.page', { page: pageIndex + 1 })}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={goNext}
                  disabled={!canNext}
                  busy={paging}
                >
                  {t('action.next')}
                </Button>
              </div>
            </div>
          </div>
        </QueryState>
      </Panel>
    </Page>
  )
}

/** The target column collapses the possible subjects into one cell, because a
 *  row touches at most one or two of them and five mostly-empty columns cost
 *  more width than they return.
 *
 *  Every subject is an identifier and stays one. The trail stores ids and the
 *  handler serves them unjoined, so the names this column used to reach for —
 *  a project name, an account label, a model name — were never in the response;
 *  each one fell through to the id it was meant to replace. Showing the id and
 *  saying what kind of thing it is beats showing an id that pretends to be a
 *  name. */
function AuditTarget({ entry }: { entry: AuditEntry }) {
  const parts: Array<{ key: string; node: ReactNode }> = []

  if (entry.projectId) {
    parts.push({
      key: 'project',
      node: (
        <Link to={`/projects/${entry.projectId}/overview`} className="k-mono">
          {entry.projectId.slice(0, 8)}
        </Link>
      ),
    })
  }
  if (entry.taskId) {
    parts.push({
      key: 'task',
      // Plain mono, not a link: there is no /missions route (it would 404), and
      // the id is the machine value it is. If a task detail view ever exists,
      // link it here.
      node: <span className="k-mono">{entry.taskId.slice(0, 8)}</span>,
    })
  }
  if (entry.sessionId) {
    parts.push({
      key: 'session',
      node: (
        <Link to={`/live/${entry.sessionId}`} className="k-mono">
          {entry.sessionId.slice(0, 8)}
        </Link>
      ),
    })
  }
  if (entry.hostId) {
    parts.push({
      key: 'host',
      node: (
        <Link to={`/hosts/${entry.hostId}`} className="k-mono">
          {entry.hostId.slice(0, 8)}
        </Link>
      ),
    })
  }
  // The generic pair is what most rows actually carry — a setting, a role, an
  // approval — and it is the only place the *kind* of the subject is recorded.
  if (entry.targetType) {
    parts.push({
      key: 'target',
      node: (
        <span>
          {entry.targetType}
          {entry.targetId && <span className="k-mono"> {entry.targetId.slice(0, 8)}</span>}
        </span>
      ),
    })
  }

  if (parts.length === 0) return <span className="k-dash">—</span>

  return (
    <span className="k-audit__target">
      {parts.map((p) => (
        <span key={p.key}>{p.node}</span>
      ))}
    </span>
  )
}
